/**
 * WebSocket メッセンジャーイベント型定義。
 *
 * BE 仕様書「Messenger リアルタイム連携（FE 実装ガイド）」§2 および
 * 「SPA 向け WebSocket 仕様書」§3.2 / §3.3 に準拠する。
 *
 * 受信: /ws/spa/events で push される `message.<scope>.*` / `typing.<scope>.*` /
 *       `task_board.updated`（タスクボードの内容変更通知）。
 * 送信: typing.<scope>.(start|stop) のみ（メッセージ送信・編集・削除は REST）。
 */

export type UuidString = string;

/**
 * BE 仕様書の `type_offer` は "subcontract" | "master"。
 * FE の `DmThreadType` は "subcontract" | "master_contract" を使っているが、
 * 本受信型では BE payload をそのまま表現するために "master" を採用する。
 * 受信時は `dm_id` ベースで特定するので FE 側は `type_offer` を参照しなくてよい。
 */
export type DmTypeOffer = "subcontract" | "master";

// ---- 共通エンベロープ ------------------------------------------------------

/** プロジェクトトークルームのスコープ識別子。 */
export type MessengerScope =
  | "project"
  | "shot_comment"
  | "dm"
  | "chat"
  | "task_board"
  // soremon（AI アシスタント）の処理中の状態。本人にだけ届く。
  | "soremon";

// ---- Server -> Client (incoming events) -----------------------------------

export type MessengerIncomingEvent =
  | { type: "connection.ready"; payload: { user_id: number } }
  | { type: "pong"; payload: Record<string, never> }
  // Project talkroom
  | { type: "message.project.created"; payload: ProjectMessageCreatedPayload }
  | { type: "message.project.updated"; payload: ProjectMessageCreatedPayload }
  | { type: "message.project.deleted"; payload: ProjectMessageDeletedPayload }
  | { type: "message.project.read"; payload: ProjectMessageReadPayload }
  | { type: "message.project.download_read"; payload: ProjectMessageDownloadReadPayload }
  // Shot comment
  | { type: "message.shot_comment.created"; payload: ShotCommentCreatedPayload }
  | { type: "message.shot_comment.updated"; payload: ShotCommentCreatedPayload }
  | { type: "message.shot_comment.deleted"; payload: ShotCommentDeletedPayload }
  | { type: "message.shot_comment.read"; payload: ShotCommentReadPayload }
  | { type: "message.shot_comment.download_read"; payload: ShotCommentDownloadReadPayload }
  // Media item（ショット配下のメディアのタグ等の更新）
  | { type: "message.media_item.updated"; payload: MediaItemUpdatedPayload }
  // DM
  | { type: "message.dm.created"; payload: DmMessageCreatedPayload }
  | { type: "message.dm.updated"; payload: DmMessageCreatedPayload }
  | { type: "message.dm.deleted"; payload: DmMessageDeletedPayload }
  | { type: "message.dm.read"; payload: DmMessageReadPayload }
  | { type: "message.dm.download_read"; payload: DmMessageDownloadReadPayload }
  // Chat (offer-independent DM)
  | { type: "message.chat.created"; payload: ChatMessageCreatedPayload }
  | { type: "message.chat.updated"; payload: ChatMessageCreatedPayload }
  | { type: "message.chat.deleted"; payload: ChatMessageDeletedPayload }
  | { type: "message.chat.read"; payload: ChatMessageReadPayload }
  | { type: "message.chat.download_read"; payload: ChatMessageDownloadReadPayload }
  // DM thread status (offer 承諾 / 辞退 / 締結 / 納品 / 検収 等の状態遷移)
  | { type: "dm.thread.updated"; payload: DmThreadUpdatedPayload }
  // Task board（他ユーザーの変更通知。受信側は GET /task-boards を再取得する）
  | { type: "task_board.updated"; payload: TaskBoardUpdatedPayload }
  // soremon: 考えている（thinking）/ ツールを実行している（acting）
  | { type: "soremon.progress"; payload: SoremonProgressPayload }
  // Typing
  | { type: "typing.project.started" | "typing.project.stopped"; payload: TypingProjectPayload }
  | {
      type: "typing.shot_comment.started" | "typing.shot_comment.stopped";
      payload: TypingShotCommentPayload;
    }
  | { type: "typing.dm.started" | "typing.dm.stopped"; payload: TypingDmPayload }
  | {
      type: "typing.task_board.started" | "typing.task_board.stopped";
      payload: TypingTaskBoardPayload;
    };

export interface ProjectMessageCreatedPayload {
  project_id: UuidString;
  message_id: UuidString;
  parent_id: UuidString | null;
  sender_user_id: number | null;
}
export interface ProjectMessageDeletedPayload {
  project_id: UuidString;
  message_id: UuidString;
}
export interface ProjectMessageReadPayload {
  project_id: UuidString;
  message_ids: UuidString[];
  reader_user_id: number;
}
export interface ProjectMessageDownloadReadPayload {
  project_id: UuidString;
  message_ids: UuidString[];
  file_ids: UuidString[];
  reader_user_id: number;
}

export interface ShotCommentCreatedPayload {
  project_id: UuidString;
  shot_id: UuidString;
  comment_id: UuidString;
  parent_id: UuidString | null;
  sender_user_id: number | null;
}
export interface ShotCommentDeletedPayload {
  project_id: UuidString;
  shot_id: UuidString;
  comment_id: UuidString;
}
export interface ShotCommentReadPayload {
  project_id: UuidString;
  shot_id: UuidString;
  comment_ids: UuidString[];
  reader_user_id: number;
}
export interface ShotCommentDownloadReadPayload {
  project_id: UuidString;
  shot_id: UuidString;
  comment_ids: UuidString[];
  file_ids: UuidString[];
  reader_user_id: number;
}

export interface MediaItemUpdatedPayload {
  project_id: UuidString;
  shot_id: UuidString;
  media_item_id: UuidString;
  producer_tag: string | null;
  client_tag: string | null;
  sender_user_id: number;
}

export interface DmMessageCreatedPayload {
  project_id: UuidString;
  dm_id: UuidString;
  message_id: UuidString;
  parent_id: UuidString | null;
  sender_user_id: number | null;
  type_offer: DmTypeOffer;
}
export interface DmMessageDeletedPayload {
  project_id: UuidString;
  dm_id: UuidString;
  message_id: UuidString;
  type_offer: DmTypeOffer;
}
export interface DmMessageReadPayload {
  project_id: UuidString;
  dm_id: UuidString;
  message_ids: UuidString[];
  reader_user_id: number;
  type_offer: DmTypeOffer;
}
export interface DmMessageDownloadReadPayload {
  project_id: UuidString;
  dm_id: UuidString;
  message_ids: UuidString[];
  file_ids: UuidString[];
  reader_user_id: number;
  type_offer: DmTypeOffer;
}

export interface ChatMessageCreatedPayload {
  project_id: UuidString;
  chat_id: UuidString;
  message_id: UuidString;
  parent_id: UuidString | null;
  sender_user_id: number | null;
}
export interface ChatMessageDeletedPayload {
  project_id: UuidString;
  chat_id: UuidString;
  message_id: UuidString;
  actor_user_id: number;
}
export interface ChatMessageReadPayload {
  project_id: UuidString;
  chat_id: UuidString;
  message_ids: UuidString[];
  reader_user_id: number;
}
export interface ChatMessageDownloadReadPayload {
  project_id: UuidString;
  chat_id: UuidString;
  message_ids: UuidString[];
  file_ids: UuidString[];
  reader_user_id: number;
}

export interface DmThreadUpdatedPayload {
  project_id: UuidString;
  dm_id: UuidString;
  type_offer: DmTypeOffer;
  /** 遷移後ステータス。旧サーバでは省略される場合がある。 */
  status?: string;
  /** PATCH 前ステータス。旧サーバでは省略される場合がある。 */
  prev_status?: string;
  /** 操作者ユーザー ID。旧サーバでは省略される場合がある。 */
  actor_user_id?: number;
}

export interface TypingProjectPayload {
  project_id: UuidString;
  parent_id: UuidString | null;
  user_id: number;
}
export interface TypingShotCommentPayload {
  project_id: UuidString;
  shot_id: UuidString;
  parent_id: UuidString | null;
  user_id: number;
}
export interface TypingDmPayload {
  project_id: UuidString;
  dm_id: UuidString;
  parent_id: UuidString | null;
  user_id: number;
  type_offer: DmTypeOffer;
}
/** タスクボードのセル編集中 presence。セルは task_id + column_id で特定する。 */
export interface TypingTaskBoardPayload {
  project_id: UuidString;
  task_id: string;
  /** ボードの列 ID。タイトル列は予約値 `"__title"`。 */
  column_id: string;
  user_id: number;
}

/** soremon の処理中の状態（BE soremon/agent.py の _progress）。 */
export interface SoremonProgressPayload {
  conversation_id: number;
  state: "thinking" | "acting";
  /** acting のとき実行中の操作名（例: "タスクボードを確認"）。thinking は空のことが多い */
  label: string;
}

/** タスクボードの内容変更通知（ボード / 列 / タスクの CRUD・並び替え）。 */
export interface TaskBoardUpdatedPayload {
  project_id: UuidString;
  /** 変更対象ボード。並び替えなど複数ボードにまたがる変更は null。 */
  board_id: UuidString | null;
  /** 操作したユーザー（送信者本人には配信されない）。 */
  user_id: number;
}

// ---- Client -> Server (outgoing events) -----------------------------------

export type MessengerOutgoingEvent =
  | { type: "ping" }
  | {
      type: "typing.project.start" | "typing.project.stop";
      payload: { project_id: UuidString; parent_id?: UuidString | null };
    }
  | {
      type: "typing.shot_comment.start" | "typing.shot_comment.stop";
      payload: {
        project_id: UuidString;
        shot_id: UuidString;
        parent_id?: UuidString | null;
      };
    }
  | {
      type: "typing.dm.start" | "typing.dm.stop";
      payload: {
        project_id: UuidString;
        dm_id: UuidString;
        parent_id?: UuidString | null;
      };
    }
  | {
      type: "typing.task_board.start" | "typing.task_board.stop";
      payload: {
        project_id: UuidString;
        task_id: string;
        column_id: string;
      };
    };

// ---- 型ガード --------------------------------------------------------------

/**
 * 受信イベントの `type` を検査し、対象のスコープ + primary id を返す。
 * EventBus 側でのディスパッチに使用する。
 */
export const resolveScopeKey = (
  ev: MessengerIncomingEvent,
): { scope: MessengerScope; key: string } | null => {
  if (ev.type.startsWith("message.project.") || ev.type.startsWith("typing.project.")) {
    const payload = ev.payload as { project_id?: UuidString };
    if (!payload?.project_id) return null;
    return { scope: "project", key: payload.project_id };
  }
  if (
    ev.type.startsWith("message.shot_comment.") ||
    ev.type.startsWith("typing.shot_comment.") ||
    ev.type.startsWith("message.media_item.")
  ) {
    const payload = ev.payload as { shot_id?: UuidString };
    if (!payload?.shot_id) return null;
    return { scope: "shot_comment", key: payload.shot_id };
  }
  if (
    ev.type.startsWith("message.dm.") ||
    ev.type.startsWith("typing.dm.") ||
    ev.type.startsWith("dm.thread.")
  ) {
    const payload = ev.payload as { dm_id?: UuidString };
    if (!payload?.dm_id) return null;
    return { scope: "dm", key: payload.dm_id };
  }
  // Chat は現状 message.chat.* のみ。typing.chat.* は BE 未実装のため扱わない。
  if (ev.type.startsWith("message.chat.")) {
    const payload = ev.payload as { chat_id?: UuidString };
    if (!payload?.chat_id) return null;
    return { scope: "chat", key: payload.chat_id };
  }
  // タスクボード（内容変更 + セル編集中 presence）はプロジェクト単位で購読する。
  if (
    ev.type.startsWith("task_board.") ||
    ev.type.startsWith("typing.task_board.")
  ) {
    const payload = ev.payload as { project_id?: UuidString };
    if (!payload?.project_id) return null;
    return { scope: "task_board", key: payload.project_id };
  }
  if (ev.type.startsWith("soremon.")) {
    const payload = ev.payload as { conversation_id?: number };
    if (payload?.conversation_id == null) return null;
    return { scope: "soremon", key: String(payload.conversation_id) };
  }
  return null;
};
